import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import { Popover, PopoverPanel, PopoverTrigger } from '@clay-run/glaze/popover';
import { Text } from '@clay-run/glaze/text';
import { Textarea } from '@clay-run/glaze/textarea';
import { effectiveNoteTextMax, mentionsInText, normalizeNoteText, type NoteMention, type NoteTarget } from '@shared/flow/notes';
import { useNotesEnabled } from '../../../../hooks/useNotesEnabled';
import { FLOW_ICONS } from '../../icons';
import { PostingToCombo, postingLabel, swapPostingPrefix } from './PostingToCombo';
import { useFlowNotesStore } from './flow-notes-store';
import { useFlowNotesThread, useMyNoteIdentity, usePostNote } from './useFlowNotes';
import { useOpenNotes } from './useOpenNotes';

type Roster = { id: string; name: string }[];

export interface SaveAsNoteButtonProps {
  /** The surface's text (an intelligence paragraph, a signal summary, a chat answer). */
  text: string;
  /** Where the note goes unless the rep changes it: the card's contact or account. */
  defaultTarget: NoteTarget;
}

const NO_ROSTER: Roster = [];

/**
 * The preview form. The roster (contacts the rep may post to instead) and the account name come from
 * the target's thread, already cached when the rail has been open; mounts only while the preview is open.
 */
function SaveAsNoteForm({ text, defaultTarget, onDone }: { text: string; defaultTarget: NoteTarget; onDone: () => void }) {
  const thread = useFlowNotesThread(defaultTarget);
  const roster = thread.data?.roster ?? NO_ROSTER;
  const rosterTruncated = thread.data?.rosterTruncated ?? false;
  const accountName = thread.data?.accountName ?? null;
  const myName = useMyNoteIdentity();
  const limit = effectiveNoteTextMax(myName ?? '');
  const [rawDraft, setDraft] = useState(() => normalizeNoteText(text));
  // Derived, so a late identity (a longer name, a smaller limit) re-clips the draft.
  const draft = rawDraft.slice(0, limit);
  // Label of the `@Name ` this form inserted itself; a rep-typed leading name is never stripped.
  const [prefixLabel, setPrefixLabel] = useState<string | null>(null);
  const [target, setTarget] = useState<NoteTarget>(defaultTarget);
  const [mentions, setMentions] = useState<NoteMention[]>([]);
  const saving = useFlowNotesStore(s => s.pending?.status === 'saving');
  const { mutate } = usePostNote();
  const openNotes = useOpenNotes();

  const changeTarget = (next: NoteTarget) => {
    const to = next.kind === 'contact' ? postingLabel(next, roster) : null;
    setDraft(swapPostingPrefix(draft, prefixLabel, to).slice(0, limit));
    setPrefixLabel(to);
    setMentions(current => [
      ...current.filter(m => m.label !== prefixLabel),
      ...(next.kind === 'contact' && to ? [{ kind: 'contact' as const, id: next.id, label: to }] : []),
    ]);
    setTarget(next);
  };

  const canPost = !saving && normalizeNoteText(draft) !== '';

  const submit = () => {
    if (!canPost) return;
    const notes = normalizeNoteText(draft);
    mutate({
      target,
      notes,
      clientNoteId: crypto.randomUUID(),
      mentions: mentionsInText(mentions, notes),
      source: 'save_as_note',
    }, { onAccepted: () => toast.success('Saved note') });
    openNotes(target, { entryPoint: 'save_as_note' });
    onDone();
  };

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        size="sm"
        resizable={false}
        rows={4}
        maxLength={limit}
        value={draft}
        aria-label="Note text"
        onChange={event => setDraft(event.target.value.slice(0, limit))}
      />
      <div className="flex items-center gap-2">
        <Text size="xs" variant="tertiary" className="shrink-0">
          Posting to
        </Text>
        <PostingToCombo
          accountId={defaultTarget.accountId}
          roster={roster}
          rosterTruncated={rosterTruncated}
          accountName={accountName}
          value={target}
          onChange={changeTarget}
        />
        <Text size="xs" variant="tertiary" className="shrink-0">
          {draft.length}/{limit}
        </Text>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" disabled={!canPost} onClick={submit}>
          Post
        </Button>
      </div>
    </div>
  );
}

function SaveAsNoteEnabled({ text, defaultTarget }: SaveAsNoteButtonProps) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    // Stop the click so a trigger inside a clickable row or card does not also fire it.
    <span className="inline-flex shrink-0" onClick={event => event.stopPropagation()}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button variant="ghost" size="sm" aria-label="Save as note">
              <Icon type={FLOW_ICONS.notes} size="sm" aria-hidden />
              Save as note
            </Button>
          }
        />
        <PopoverPanel positioner={{ align: 'end' }} className="w-80 !overflow-visible p-3">
          {open && <SaveAsNoteForm text={text} defaultTarget={defaultTarget} onDone={close} />}
        </PopoverPanel>
      </Popover>
    </span>
  );
}

/**
 * Ghost "Save as note" button + popover preview. The only way text the rep did
 * not type enters Notes; it is an ordinary note posted through `usePostNote`
 * (same ladder, same one-in-flight rule). The popover owns no timers: it posts,
 * opens the rail on the target and closes; confirmation continues in
 * `useNotesPendingConfirmation`. Renders nothing while Notes is off.
 */
export function SaveAsNoteButton(props: SaveAsNoteButtonProps) {
  const { enabled } = useNotesEnabled();
  if (!enabled) return null;
  return <SaveAsNoteEnabled {...props} />;
}
